/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, INodeProps, Modal, ModalBody, ModalFooter, ModalHeader, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IOrderTemplateLineViewProps } from './components';
import { IOrderTemplateViewProps } from './gdn-order-template';

const OrderTemplateLinesView: React.FC<IOrderTemplateViewProps> = (props: IOrderTemplateViewProps) => {
    const {
        OrderTemplateLines,
        status,
        statusMessage,
        heading,
        springOrderPlanning,
        ProductsTable,
        ProductsTableHeading,
        orderLines,
        ProductsTableRow,
        productsStatusMessage,
        OrderTemplateActions,
        orderActions,
        pagination,
        itemAddedToCartDialog,
        bulkOrderLineActions,
        isDetailsDisplayed,
        detailedProductView,
        toggleDetailsDisplayed,
        resources: {
            cancelBtnLabel,
            productNumberText,
            productText,
            productUnitPriceText,
            productUOMText,
            productQuantityText,
            productLineCatalogName
        }
    } = props;

    const toggleDetails = React.useCallback(() => {
        toggleDetailsDisplayed && toggleDetailsDisplayed(undefined);
    }, [toggleDetailsDisplayed]);

    return (
        <Module {...OrderTemplateLines}>
            {heading}
            {springOrderPlanning}
            {OrderTemplateActions && (
                <Node {...OrderTemplateActions}>
                    {orderActions?.addTemplateToBag}
                    {orderActions?.renameTemplate}
                    {orderActions?.removeTemplate}
                    {orderActions?.actionsSplitButton}
                    {!isDetailsDisplayed && productsStatusMessage}
                </Node>
            )}
            {bulkOrderLineActions}
            {status !== 'SUCCESS' && statusMessage}
            {ProductsTable && orderLines && orderLines.length > 0 && (
                <Node className='ms-order-template__content'>
                    <Node {...ProductsTable}>
                        {ProductsTableHeading}
                        <tbody>
                            {orderLines &&
                                orderLines.map(product => {
                                    return _renderItem(product, productQuantityText, ProductsTableRow);
                                })}
                        </tbody>
                    </Node>
                    {pagination}
                </Node>
            )}
            {itemAddedToCartDialog}
            <Modal
                wrapClassName='ms-order-template-mobile-view-modal'
                autoFocus
                fade={false}
                applicationNode='renderPage'
                isOpen={isDetailsDisplayed}
                toggle={toggleDetails}
            >
                <ModalHeader toggle={toggleDetails}>Product Details</ModalHeader>
                <ModalBody>
                    <ul>
                        <li>{detailedProductView?.productImage}</li>
                        <li className='product-info'>
                            <span>{productText}:</span>
                            {detailedProductView?.productInfo}
                        </li>
                        <li>
                            <span>{productLineCatalogName}:</span>
                            {detailedProductView?.productLineCatalog}
                        </li>
                        <li>
                            <span>{productNumberText}:</span>
                            {detailedProductView?.productNumber}
                        </li>
                        <li>
                            <span>{productUnitPriceText}:</span>
                            {` `}
                            {detailedProductView?.productPrice}
                        </li>
                        <li>
                            <span>{productUOMText}:</span>
                            {` `}
                            {detailedProductView?.productUnitOfMeasure}
                        </li>
                        <li>
                            <span>{productQuantityText}:</span>
                            {detailedProductView?.productQuantityView}
                        </li>
                        {productsStatusMessage && <li>{productsStatusMessage}</li>}
                    </ul>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={toggleDetails}>{cancelBtnLabel}</Button>
                </ModalFooter>
            </Modal>
        </Module>
    );
};

const _renderItem = (product: IOrderTemplateLineViewProps, quantityLabel: string, ProductsTableRow?: INodeProps): JSX.Element | null => {
    if (!product) {
        return null;
    }

    const {
        key,
        selectLine,
        productLineCatalog,
        productImage,
        productPrice,
        productNumber,
        addToBagButton,
        removeButton,
        productInfo,
        productUnitOfMeasure,
        productQuantityView,
        productQuantity,
        popUp,
        isMobile,
        extraActionsPopup,
        isMultipleCatalogFeatureEnabledInHq,
        disabled
    } = product;

    const lineVisibility = disabled
        ? {
              pointerEvents: 'none',
              opacity: 0.5
          }
        : {
              pointerEvents: 'auto',
              opacity: 1
          };

    const disableElement: React.CSSProperties = lineVisibility as React.CSSProperties;

    return (
        <Node key={key} className={`${ProductsTableRow!.className}`} {...ProductsTableRow}>
            <td>{selectLine}</td>
            {!isMobile && isMultipleCatalogFeatureEnabledInHq && <td style={disableElement}>{productLineCatalog}</td>}
            <td style={disableElement}>{productImage}</td>
            {isMobile ? (
                <td>
                    <div>{productNumber}</div>
                    <div style={disableElement}>
                        {quantityLabel}:{` `}
                        {productQuantity}
                    </div>
                </td>
            ) : (
                <td>{productNumber}</td>
            )}
            {!isMobile && <td style={disableElement}>{productInfo}</td>}
            {!isMobile && <td style={disableElement}>{productPrice}</td>}
            {!isMobile && <td style={disableElement}>{productUnitOfMeasure}</td>}
            {!isMobile && <td style={disableElement}>{productQuantityView}</td>}
            {isMobile && <td style={disableElement}>{popUp}</td>}
            {!isMobile && !isMultipleCatalogFeatureEnabledInHq && (
                <>
                    <td style={disableElement}>{addToBagButton}</td>
                    <td>{removeButton}</td>
                </>
            )}
            <td style={disableElement}>{extraActionsPopup}</td>
        </Node>
    );
};

export default OrderTemplateLinesView;
